import React, { useCallback, useState } from 'react';
import {
    BookingCalendarConfig,
    BookingSummaryConfig,
    languageData,
    LanguageTag,
    Platform
} from '~types';
import { differenceInCalendarDays, format } from 'date-fns';
import { fullDateWithDashes } from '~constants';
import {
    BookingRequestParamsType,
    createBookingRequest
} from '~api/getBookingCalendars';
import { adTracking } from '~tracking';
import { captureExceptionNewRelic } from '~components/logger';
import { ResponseType } from '~components/BookingSummary/types';
import { BOOKING_SUMMARY_LOCAL_STORAGE } from '~components/BookingSummary/constants';
import { TrackEventFn, useAppContext } from '~state/App.context';
import { useHistory } from 'react-router-dom';
import { stringifyQuery } from '~utils/stringifyQuery';
import { useIntl, useLocalisationContext } from '@olxpwa-lib/i18n/client';
import { useSendMessage } from '@olxeu-u2u/chat-react-js-sdk/dist/api/messagesApi';
import { AdData } from '@olxpwa-lib/utils-ad/types';
import { BookingCalendarFormValue } from '~components/AdServiceBooking/types';
import { trackCategories, trackAdParams, trackPriceParams } from '~utils';
import { useCategories } from '@olxpwa-lib/categories';
import { useGetExistingConversationId } from '@olxeu-u2u/chat-react-js-sdk/dist/api/conversationApi';

export const formatDateString = (value: Date, locale: LanguageTag): string => {
    return format(value, 'dd MMM, yyyy', { locale: languageData[locale] });
};

const formatBookingDate = (
    dateFrom: Date | undefined,
    dateTo: Date | undefined,
    locale: LanguageTag
) => {
    if (dateFrom && dateTo) {
        return `${formatDateString(dateFrom, locale)} - ${formatDateString(
            dateTo,
            locale
        )}`;
    }

    return dateFrom ? formatDateString(dateFrom, locale) : '';
};

const getDaysNightsCount = (
    dateFrom: Date,
    dateTo: Date | undefined,
    isDaysCount: boolean
) => {
    if (dateFrom && dateTo) {
        const differenceInDays = differenceInCalendarDays(dateTo, dateFrom);
        return isDaysCount ? differenceInDays + 1 : differenceInDays;
    }

    return 1;
};

export type BookingCalendarHandleSubmitTypes = {
    showAvailability: boolean;
    ad: AdData | null;
    isStr: boolean;
    displayBlockDatesFeature: boolean;
    isGuestsFeatureEnabled: boolean;
    reset?: () => void;
    onBlockDates?: (
        values: BookingCalendarFormValue & {
            requestParams: BookingRequestParamsType;
        }
    ) => void;
};

export const useBookingCalendarHandleSubmit = ({
    showAvailability,
    ad,
    isStr = false,
    displayBlockDatesFeature = false,
    isGuestsFeatureEnabled = false,
    reset,
    onBlockDates
}: BookingCalendarHandleSubmitTypes) => {
    const { formatMessage } = useIntl();
    const { getAuthToken, appConfig, authenticated, tracker, PWAutils } =
        useAppContext();
    const [categories] = useCategories();
    const { trackEvent, getTouchPointPage } = tracker;
    const { redirectToLogin, dispatchToastMessage, trackingParams } = PWAutils;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [responseState, setResponseState] = useState<ResponseType>(
        ResponseType.notRequested
    );
    const history = useHistory();
    const isMobile = appConfig.platform === Platform.MOBILE;
    const sendChatMessage = useSendMessage();
    const getExistingConversationId = useGetExistingConversationId();
    const { env, locale } = appConfig;
    const {
        marketMetadata: { countryCode }
    } = useLocalisationContext();

    const getToken = useCallback(async () => {
        const authToken = await getAuthToken();

        return authToken?.accessToken;
    }, [getAuthToken]);

    const openChatPopup = useCallback(async () => {
        if (isMobile) {
            if (!ad?.id) {
                return;
            }
            const conversationId = await getExistingConversationId?.(
                ad.id.toString()
            ).catch(error => captureExceptionNewRelic(error));
            conversationId &&
                history.push(`/myaccount/answer/${conversationId}`);

            return;
        }
        const queryStr = stringifyQuery(history, {
            chat: 1,
            isPreviewActive: 0
        });

        history.replace(`${history.location.pathname}?${queryStr}`);
    }, [history, isMobile]);
    const showToastMessage = useCallback(
        (
            trackEvent: TrackEventFn,
            dateFrom: Date,
            dateTo?: Date,
            bookingId?: string,
            options?: {
                bookingSummary: boolean;
                dispatchOnlyTrackingEvent: boolean;
            }
        ) => {
            const { bookingSummary, dispatchOnlyTrackingEvent } = options || {};

            const chatReplyTracking = (bookingSummary = false) => {
                const touchPointButton = () => {
                    if (showAvailability || bookingSummary) {
                        return adTracking.touchPointButton
                            .bookingAvailabilityCalendar;
                    } else {
                        return adTracking.touchPointButton.bookingBasicCalendar;
                    }
                };
                return trackEvent(adTracking.events.replyChatSent, {
                    ...trackCategories(categories, ad?.category?.id ?? null),
                    ...trackPriceParams(ad?.price ?? null),
                    ...trackAdParams(ad),
                    ...trackingParams,
                    touch_point_button: touchPointButton(),
                    touch_point_page: bookingSummary
                        ? 'request_details'
                        : getTouchPointPage(),
                    page_title_name: ad?.title,
                    check_in_date: new Date(
                        format(dateFrom, fullDateWithDashes, {
                            locale: languageData[locale]
                        })
                    ).toISOString(),
                    check_out_date: dateTo
                        ? new Date(
                              format(dateTo, fullDateWithDashes, {
                                  locale: languageData[locale]
                              })
                          ).toISOString()
                        : undefined,
                    booking_request_id: bookingId
                });
            };

            const message = () => {
                if (bookingSummary) {
                    return formatMessage(
                        {
                            id: 'service.booking.summary.sucess.title',
                            defaultMessage:
                                'A request for reservation has been sent to the vendor. {br}  You can check the details in your chat messages.'
                        },
                        {
                            br: <br />
                        }
                    );
                }

                return formatMessage({
                    id: 'service.booking.ad.request.button.success.message',
                    defaultMessage:
                        'The availability request has been sent to the provider.'
                });
            };
            chatReplyTracking(bookingSummary);

            // it is used for sending the tracking event without showing the toast message on the desktop
            if (dispatchOnlyTrackingEvent) {
                return null;
            }

            dispatchToastMessage?.(message() as string, 10000, 'center');
        },
        [
            dispatchToastMessage,
            categories,
            ad,
            locale,
            showAvailability,
            formatMessage,
            trackingParams
        ]
    );

    const sendMessages = useCallback(
        async (dateFrom: Date, dateTo?: Date, note?: string) => {
            const countFormat = isStr ? 'nights' : 'days';

            return await sendChatMessage(
                {
                    message: {
                        text: formatMessage(
                            {
                                id: isStr
                                    ? 'service.booking.ad.nights.chat.message.text'
                                    : 'service.booking.ad.days.chat.message.text',
                                defaultMessage: isStr
                                    ? 'Is it available to rent for the following dates {date} ({nights, number} {nights, plural, one {night} few {nights} other {nights}})?'
                                    : 'Is it available to rent for the following dates {date} ({days, number} {days, plural, one {day} few {days} other {days}})?'
                            },
                            {
                                date: formatBookingDate(
                                    dateFrom,
                                    dateTo,
                                    locale
                                ),
                                [countFormat]: getDaysNightsCount(
                                    dateFrom,
                                    dateTo,
                                    false
                                )
                            }
                        )
                    }
                },
                undefined,
                ad?.id?.toString()
            )
                .then(async () => {
                    if (note) {
                        await sendChatMessage(
                            {
                                message: {
                                    text: note
                                }
                            },
                            undefined,
                            ad?.id?.toString()
                        );
                    }
                })
                .finally(async () => {
                    await openChatPopup();
                });
        },
        [ad?.id, formatMessage, isStr, locale, openChatPopup, sendChatMessage]
    );

    const onSubmitWithAvailability = useCallback<BookingCalendarConfig>(
        async ({ dateFrom, dateTo, note }) => {
            if (!authenticated) {
                redirectToLogin?.();
                return;
            }

            try {
                const token = await getToken();

                if (!dateFrom || !token) {
                    return;
                }
                setIsLoading(true);
                const requestParams = {
                    env,
                    token,
                    locale,
                    offerId: ad?.id ?? 0,
                    countryCode,
                    dateFrom: format(dateFrom, fullDateWithDashes, {
                        locale: languageData[locale]
                    }),
                    dateTo: format(
                        dateTo ? dateTo : dateFrom,
                        fullDateWithDashes,
                        {
                            locale: languageData[locale]
                        }
                    ),
                    note,
                    isStr
                };
                if (displayBlockDatesFeature && onBlockDates) {
                    onBlockDates({ dateFrom, dateTo, requestParams });
                } else {
                    const response = await createBookingRequest(requestParams);
                    if (response.id) {
                        showToastMessage(
                            trackEvent,
                            dateFrom,
                            dateTo,
                            response.id
                        );
                    }
                }
            } catch (error) {
                captureExceptionNewRelic(error as Error);
                dispatchToastMessage?.(
                    formatMessage({
                        id: 'error500.title',
                        defaultMessage: 'Oops something went wrong'
                    }),
                    10000,
                    'center'
                );
            } finally {
                reset?.();
                setIsLoading(false);
            }
        },
        [
            ad?.id,
            authenticated,
            countryCode,
            displayBlockDatesFeature,
            env,
            formatMessage,
            getToken,
            isStr,
            locale,
            redirectToLogin,
            showToastMessage,
            trackEvent,
            onBlockDates
        ]
    );

    const onSubmitBookingSummary = useCallback<BookingSummaryConfig>(
        async ({ dateFrom, dateTo, note, guests }) => {
            if (!authenticated) {
                redirectToLogin?.();
                return;
            }

            try {
                const token = await getToken();

                if (!dateFrom || !token) {
                    return;
                }
                setIsLoading(true);
                const requestParams = {
                    env,
                    token,
                    locale,
                    offerId: ad?.id ?? 0,
                    countryCode,
                    dateFrom: format(dateFrom, fullDateWithDashes, {
                        locale: languageData[locale]
                    }),
                    dateTo: format(
                        dateTo ? dateTo : dateFrom,
                        fullDateWithDashes,
                        {
                            locale: languageData[locale]
                        }
                    ),
                    note,
                    isStr,
                    guests:
                        guests && isGuestsFeatureEnabled
                            ? { guests }
                            : { guests: { adults: 0, children: 0, infants: 0 } }
                };

                const response = await createBookingRequest(requestParams);
                if (response.id) {
                    showToastMessage(
                        trackEvent,
                        dateFrom,
                        dateTo,
                        response.id,
                        {
                            bookingSummary: true,
                            dispatchOnlyTrackingEvent: !isMobile
                        }
                    );
                    if (isMobile) {
                        history.push(`/${ad?.id}`);
                    } else {
                        setResponseState(ResponseType.success);
                    }
                    localStorage.removeItem(BOOKING_SUMMARY_LOCAL_STORAGE);
                }
            } catch (error) {
                setResponseState(ResponseType.error);
                captureExceptionNewRelic(error as Error);
                isMobile &&
                    dispatchToastMessage?.(
                        formatMessage({
                            id: 'service.booking.summary.toaster.error',
                            defaultMessage:
                                'Oops! Something went wrong. Request was not sent. Please try again.'
                        }),
                        10000,
                        'center'
                    );
            } finally {
                setIsLoading(false);
            }
        },
        [
            authenticated,
            redirectToLogin,
            getToken,
            env,
            locale,
            ad?.id,
            countryCode,
            isStr,
            isMobile,
            showToastMessage,
            trackEvent,
            history,
            dispatchToastMessage,
            formatMessage
        ]
    );

    const onSubmit = useCallback<BookingCalendarConfig>(
        async ({ dateFrom, dateTo, note }) => {
            if (!authenticated) {
                redirectToLogin?.();
                return;
            }

            try {
                if (!dateFrom) {
                    return;
                }

                setIsLoading(true);
                await sendMessages(dateFrom, dateTo, note);
                showToastMessage(trackEvent, dateFrom, dateTo);
                reset?.();
            } catch (error) {
                captureExceptionNewRelic(error as Error);
                dispatchToastMessage?.(
                    formatMessage({
                        id: 'error500.title',
                        defaultMessage: 'Oops something went wrong'
                    }),
                    10000,
                    'center'
                );
            } finally {
                setIsLoading(false);
            }
        },
        [
            authenticated,
            dispatchToastMessage,
            formatMessage,
            redirectToLogin,
            reset,
            sendMessages,
            showToastMessage,
            trackEvent
        ]
    );

    return {
        onSubmit,
        onSubmitWithAvailability,
        onSubmitBookingSummary,
        showToastMessage,
        isLoading,
        responseState
    };
};
